import React from 'react';
import { Button } from '../../components/ui/button';
import { motion } from 'framer-motion';
import {
  Megaphone,
  Plus,
  Trash2,
  Edit,
  RefreshCw,
  Calendar,
  Clock,
  ToggleLeft,
  ToggleRight,
  Link as LinkIcon,
  Image,
  Code,
} from 'lucide-react';

export const BannersTab = ({
  isItalian,
  banners,
  loadingBanners,
  handleOpenBannerDialog,
  handleToggleBanner,
  handleDeleteBanner,
  getBannerTypeLabel,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#0a0d15] border border-white/10"
    >
      <div className="p-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="font-heading text-lg uppercase tracking-wider text-white flex items-center gap-2">
          <Megaphone size={18} className="text-[#FFEA00]" />
          {isItalian ? 'Gestione Banner' : 'Banner Management'}
        </h3>
        <Button
          onClick={() => handleOpenBannerDialog()}
          className="btn-primary"
          data-testid="add-banner-btn"
        >
          <Plus size={16} className="mr-2" />
          {isItalian ? 'Nuovo Banner' : 'New Banner'}
        </Button>
      </div>

      <div className="p-4">
        {loadingBanners ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-[#00F0FF]" />
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Megaphone size={48} className="mx-auto mb-4 opacity-50" />
            <p>{isItalian ? 'Nessun banner creato' : 'No banners created'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map((banner) => {
              const typeInfo = getBannerTypeLabel(banner.banner_type);
              const now = new Date();
              const startDate = banner.start_date ? new Date(banner.start_date) : null;
              const endDate = banner.end_date ? new Date(banner.end_date) : null;
              const isScheduled = startDate && startDate > now;
              const isExpired = endDate && endDate < now;

              return (
                <div
                  key={banner.banner_id}
                  className={`p-4 border ${
                    !banner.is_active || isExpired
                      ? 'bg-slate-800/30 border-slate-700/50 opacity-60'
                      : isScheduled
                      ? 'bg-[#FFEA00]/5 border-[#FFEA00]/30'
                      : 'bg-[#050508] border-white/10'
                  }`}
                  data-testid={`banner-item-${banner.banner_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs rounded border ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                        {!banner.is_active && (
                          <span className="px-2 py-0.5 text-xs rounded bg-slate-700 text-slate-400 border border-slate-600">
                            {isItalian ? 'Disattivo' : 'Inactive'}
                          </span>
                        )}
                        {isScheduled && (
                          <span className="px-2 py-0.5 text-xs rounded bg-[#FFEA00]/20 text-[#FFEA00] border border-[#FFEA00]/30">
                            {isItalian ? 'Programmato' : 'Scheduled'}
                          </span>
                        )}
                        {isExpired && (
                          <span className="px-2 py-0.5 text-xs rounded bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30">
                            {isItalian ? 'Scaduto' : 'Expired'}
                          </span>
                        )}
                        <span className="text-xs text-slate-500">
                          {isItalian ? 'Priorità' : 'Priority'}: {banner.priority}
                        </span>
                      </div>

                      <h4 className="font-bold text-white text-sm uppercase tracking-wide">
                        {banner.title}
                      </h4>
                      <p className="text-slate-400 text-sm mt-1 line-clamp-2">
                        {banner.message}
                      </p>

                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-500">
                        {banner.link && (
                          <span className="flex items-center gap-1">
                            <LinkIcon size={12} />
                            {isItalian ? 'Con link' : 'Has link'}
                          </span>
                        )}
                        {banner.html_content && (
                          <span className="flex items-center gap-1 text-[#00F0FF]">
                            <Code size={12} />
                            HTML
                          </span>
                        )}
                        {banner.image_url && (
                          <span className="flex items-center gap-1">
                            <Image size={12} />
                            {isItalian ? 'Con immagine' : 'Has image'}
                          </span>
                        )}
                        {startDate && (
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {isItalian ? 'Inizio' : 'Start'}: {startDate.toLocaleDateString()}
                          </span>
                        )}
                        {endDate && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {isItalian ? 'Fine' : 'End'}: {endDate.toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleBanner(banner)}
                        className={banner.is_active ? 'text-[#00E676] hover:text-[#00E676]/80' : 'text-slate-400 hover:text-white'}
                        data-testid={`toggle-banner-${banner.banner_id}`}
                      >
                        {banner.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleOpenBannerDialog(banner)}
                        className="text-[#00F0FF] hover:text-[#00F0FF]/80"
                        data-testid={`edit-banner-${banner.banner_id}`}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteBanner(banner.banner_id)}
                        className="text-[#FF1744] hover:text-[#FF1744]/80"
                        data-testid={`delete-banner-${banner.banner_id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
};
